import i18next from 'i18next';
import parseHTMLStringToReact from 'htmr';
import type { ReactElement } from 'react';

import { sanitize } from '@luna/utils';

import type { Options, TOptions } from './types';
import { transformReactElementOptionsToHtml } from './translationUtils';
import { overrideSuffixSingleton, resolveKey } from './overrideSuffixService';

/** we are setting the default return type as 'string' instead of 'string | ReactElement'
 * to avoid the boilerplate of having to cast the return type
 * to string in most cases */
type DefaultReturnType = string;

function hasHtml(translation: string): boolean {
    return /<.*?>/.test(translation);
}

function isNonNullObject(translation: unknown): boolean {
    return typeof translation === 'object' && translation !== null;
}

function processTranslationObject<ReturnType>(
    translationObject: Record<string, any>,
    returnObjectAsArray?: boolean,
): ReturnType {
    const result = {};
    Object.entries(translationObject).forEach(([key, value]) => {
        if (isNonNullObject(value)) {
            // @ts-expect-error no-implicit-any
            result[key] = processTranslationObject(value as Record<string, unknown>);
        } else {
            // @ts-expect-error no-implicit-any
            result[key] = processTranslationSimpleValue(value as string);
        }
    });
    if (returnObjectAsArray) {
        return Object.values(result) as ReturnType;
    }
    return result as ReturnType;
}

function processTranslationSimpleValue<ReturnType>(translation: string): ReturnType {
    const translationSanitized = sanitize(translation);
    const hasHTML = hasHtml(translationSanitized);

    if (!hasHTML) {
        return translation as unknown as ReturnType;
    }

    return parseHTMLStringToReact(translationSanitized) as unknown as ReturnType;
}

// overload which returns array
export function t<ReturnType = string, TInterpolationMap extends Options = Options>(
    key: string,
    options: Omit<TOptions<TInterpolationMap>, 'returnObjectAsArray'> & {
        returnObjectAsArray: true;
    },
): ReturnType[];

// overload which returns object
export function t<
    ReturnType extends Record<string, any>,
    TInterpolationMap extends Options = Options,
>(
    key: string,
    options: Omit<TOptions<TInterpolationMap>, 'returnObjects'> & { returnObjects: true },
): ReturnType;

/**
 * The @luna translation primitive.
 * Retrieves translations based on the provided key and options.
 *
 * @template ReturnType - The expected return type of the translation.
 * @template TInterpolationMap - The type of interpolation options.
 *
 * @param {string} key - The translation key to look up.
 * @param {TOptions<TInterpolationMap>} [options] - Optional configuration for the translation.
 *
 * @returns {ReturnType}
 * - A string when the translation value is just a string.
 * - A React Element when the translation value is a string containing HTML.
 * - An object when returnObjects is true.
 * - An array when returnObjectAsArray is true.
 *
 * @example
 * // Basic usage (most common)
 * const simpleTranslation = t('account.balance.notAvailable');
 *
 * @example
 * // With interpolation
 * const interpolatedTranslation = t('greeting', { name: 'James' });
 *
 * @example
 * // Returning a React Element for HTML content
 * const htmlTranslation = t<ReactElement>('translationKeyWithRichTextContent');
 *
 * @example
 * // Returning an object
 * const objectTranslation = t<Record<string, string>>('nestedContent', { returnObjects: true });
 *
 * @example
 * // Returning an array
 * const arrayTranslation = t<string[]>('listItems', { returnObjectAsArray: true });
 */
export function t<
    ReturnType extends string = DefaultReturnType,
    TInterpolationMap extends Options = Options,
>(key: string, options?: TOptions<TInterpolationMap>): ReturnType;

// overload which returns string | ReactElement, can be used with t<ReactElement>('translationWithHTML')
export function t<
    ReturnType extends string | ReactElement | Record<string, unknown> = DefaultReturnType,
    TInterpolationMap extends Options = Options,
>(key: string, options?: TOptions<TInterpolationMap>): ReturnType;

export function t<ReturnType = DefaultReturnType, TInterpolationMap extends Options = Options>(
    key: string,
    options?: TOptions<TInterpolationMap>,
): ReturnType {
    const optionsCopy = {
        ...(options ?? {}),
    } as TOptions<TInterpolationMap>;

    if (optionsCopy.returnObjectAsArray) {
        optionsCopy.returnObjects = true;
    }

    const translation = i18next.t(
        resolveKey(key, overrideSuffixSingleton.get()),
        transformReactElementOptionsToHtml(optionsCopy) ?? {},
    ) as unknown;

    if (isNonNullObject(translation)) {
        return processTranslationObject<ReturnType>(
            translation as Record<string, unknown>,
            optionsCopy?.returnObjectAsArray,
        );
    }

    return processTranslationSimpleValue<ReturnType>(translation as string);
}
